import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pb } from './pocketbase'

// Types
export interface StudentBasicInfo {
  id: string
  name: string
  username: string
  email: string
  avatar: string
  points_balance: number
  remarks: string
}

export interface TeacherStats {
  points_to_distribute: number
  total_given: number
  students_helped: number
}

export interface DistributePointsRequest {
  student_id: string
  amount: number
}

export interface UpdateRemarksRequest {
  student_id: string
  remarks: string
}

export interface UpdateRemarksResponse {
  success: boolean
  message: string
}

export interface DistributePointsResponse {
  success: boolean
  teacher_points_left: number
  student_new_balance: number
  message: string
}

export interface DistributionHistoryEntry {
  id: string
  student_id: string
  student_name: string
  amount: number
  created: string
}

// Query keys
export const teacherQueryKeys = {
  students: ['teacher', 'students'] as const,
  stats: ['teacher', 'stats'] as const,
  history: ['teacher', 'history'] as const,
} as const

// Fetch all students (teacher only)
export const useStudents = () => {
  return useQuery<StudentBasicInfo[]>({
    queryKey: teacherQueryKeys.students,
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
      const response = await fetch(`${baseUrl}/api/teacher/students`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch students')
      }

      return response.json()
    },
    staleTime: 1000 * 60, // 1 minute
  })
}

// Fetch teacher stats
export const useTeacherStats = () => {
  return useQuery<TeacherStats>({
    queryKey: teacherQueryKeys.stats,
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
      const response = await fetch(`${baseUrl}/api/teacher/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch teacher stats')
      }

      return response.json()
    },
    staleTime: 1000 * 30, // 30 seconds
  })
}

// Fetch distribution history
export const useDistributionHistory = () => {
  return useQuery<DistributionHistoryEntry[]>({
    queryKey: teacherQueryKeys.history,
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
      const response = await fetch(`${baseUrl}/api/teacher/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch distribution history')
      }

      return response.json()
    },
    staleTime: 1000 * 60, // 1 minute
  })
}

// Distribute points mutation
export const useDistributePoints = () => {
  const queryClient = useQueryClient()

  return useMutation<DistributePointsResponse, Error, DistributePointsRequest>({
    mutationFn: async (request: DistributePointsRequest) => {
      const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
      const response = await fetch(`${baseUrl}/api/teacher/distribute-points`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to distribute points')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: teacherQueryKeys.students })
      queryClient.invalidateQueries({ queryKey: teacherQueryKeys.stats })
      queryClient.invalidateQueries({ queryKey: teacherQueryKeys.history })
    },
  })
}

// Update student remarks mutation
export const useUpdateRemarks = () => {
  const queryClient = useQueryClient()

  return useMutation<UpdateRemarksResponse, Error, UpdateRemarksRequest>({
    mutationFn: async (request: UpdateRemarksRequest) => {
      const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
      const response = await fetch(`${baseUrl}/api/teacher/update-remarks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update remarks')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate students query to refresh data
      queryClient.invalidateQueries({ queryKey: teacherQueryKeys.students })
    },
  })
}
